import { Column } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';

import { useTableContext } from '@components/Table/TableContext';

type useTableDataForFilterValuesProps<TData, TValue> = {
  filterData?: TValue[];
  column?: Column<TData, unknown>;
  dataChangeTriggerFn?: () => string;
  uniqueIdKey?: string | '_primitive';
  mappingFn?: (value: unknown) => TValue;
};

export function useTableDataForFilterValues<TData, TValue>({
  filterData,
  column,
  dataChangeTriggerFn,
  uniqueIdKey = 'id',
  mappingFn,
}: useTableDataForFilterValuesProps<TData, TValue>) {
  const facetedUniqueValues = column?.getFacetedUniqueValues();
  const { _rawData } = useTableContext<TData>();

  const [values, setValues] = useState<TValue[]>(filterData || []);
  const previousTriggerWatchValue = useRef<string | null>(null);
  const currentDataModel = useRef<TData[] | null>(_rawData);

  const currentTriggerWatchValue = dataChangeTriggerFn?.() || null;

  useEffect(() => {
    if (filterData) setValues(filterData);
    else {
      const shouldUpdate =
        !dataChangeTriggerFn ||
        previousTriggerWatchValue?.current !== currentTriggerWatchValue ||
        _rawData !== currentDataModel.current;

      if (shouldUpdate) {
        const uniqueColumnValues = Array.from((facetedUniqueValues || new Map()).keys());
        const newValues = uniqueColumnValues.map(
          mappingFn ||
            ((val) =>
              ({
                id: val,
                label: val,
              }) as TValue),
        );

        currentDataModel.current = _rawData;
        if (dataChangeTriggerFn) {
          if (!newValues.length) {
            previousTriggerWatchValue.current = null;
          } else {
            previousTriggerWatchValue.current = currentTriggerWatchValue;
          }
        }

        setValues(newValues);
      }
    }
  }, [
    facetedUniqueValues,
    _rawData,
    filterData,
    mappingFn,
    currentTriggerWatchValue,
    uniqueIdKey,
    dataChangeTriggerFn,
    column,
  ]);

  return { values };
}
